import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  IconBtn,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  useToast,
} from "../../../design-system";
import {
  createVacationPolicy,
  updateVacationPolicy,
} from "../../../helpers/kronaly_helper";
import {
  ACCRUAL_MODES,
  ACCRUAL_UNITS,
  DAY_COUNT_BASES,
  DISPLAY_UNITS,
  MONTHS,
  defaultPolicy,
} from "./vacationPolicyHelpers";

const SECTION_TITLES = [
  { id: "basic", label: "Información básica" },
  { id: "cycle", label: "Configuración del ciclo" },
  { id: "counter", label: "Configuración del contador" },
  { id: "carryover", label: "Tiempo acumulado" },
  { id: "notice", label: "Antelación" },
  { id: "tenure", label: "Períodos de antigüedad" },
];

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const toNumber = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toApiPayload = (form) => {
  const payload = {
    name: form.name.trim(),
    accrual_type: form.accrual_type,
    allowance_per_year: toNumber(form.allowance_per_year) ?? 0,
    cycle_start_month: toNumber(form.cycle_start_month) ?? 1,
    cycle_duration_months: toNumber(form.cycle_duration_months) ?? 12,
    display_unit: form.display_unit,
    day_count_basis: form.day_count_basis,
    unlimited: Boolean(form.unlimited),
    holiday_inside_absence_counts: Boolean(form.holiday_inside_absence_counts),
    prorate_first_year: Boolean(form.prorate_first_year),
    max_balance: toNumber(form.max_balance),
    accrual_mode: form.accrual_mode,
    allow_usage_in_next_cycle: Boolean(form.allow_usage_in_next_cycle),
    allow_negative_balance: Boolean(form.allow_negative_balance),
    carryover_amount: toNumber(form.carryover_amount) ?? 0,
    carryover_unlimited: Boolean(form.carryover_unlimited),
    carryover_expiration_months: form.carryover_unlimited
      ? null
      : toNumber(form.carryover_expiration_months),
    min_notice_days: toNumber(form.min_notice_days) ?? 0,
    tenure_periods: (form.tenure_periods || [])
      .map((p) => ({
        years_of_service: toNumber(p.years_of_service),
        extra_amount: toNumber(p.extra_amount),
        unit: p.unit || "days",
      }))
      .filter((p) => p.years_of_service && p.extra_amount !== null),
  };
  return payload;
};

const VacationPolicyEditorModal = ({ open, policy, onClose, onSaved }) => {
  const toast = useToast();
  const isEditing = Boolean(policy?.id);
  const [form, setForm] = useState(defaultPolicy);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (policy && policy.id) {
      setForm({
        ...defaultPolicy(),
        ...policy,
        tenure_periods: Array.isArray(policy.tenure_periods)
          ? policy.tenure_periods
          : [],
      });
    } else {
      setForm(defaultPolicy());
    }
    setError(null);
  }, [open, policy]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addTenurePeriod = () => {
    set({
      tenure_periods: [
        ...(form.tenure_periods || []),
        { years_of_service: 1, extra_amount: 1, unit: form.accrual_type || "days" },
      ],
    });
  };
  const updateTenurePeriod = (i, patch) =>
    set({
      tenure_periods: form.tenure_periods.map((p, idx) =>
        idx === i ? { ...p, ...patch } : p,
      ),
    });
  const removeTenurePeriod = (i) =>
    set({ tenure_periods: form.tenure_periods.filter((_, idx) => idx !== i) });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = toApiPayload(form);
      const r = isEditing
        ? await updateVacationPolicy(policy.id, payload)
        : await createVacationPolicy(payload);
      const saved = unwrap(r);
      onSaved(saved, !isEditing);
      toast.success(
        isEditing ? "Política actualizada" : "Política creada",
        saved?.name,
      );
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo guardar la política.");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = form.name.trim().length > 0 && !saving;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? "Editar política de vacaciones" : "Nueva política de vacaciones"}
      size="xl"
    >
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SectionCard title={SECTION_TITLES[0].label} hint="Nombre de la política. Útil para diferenciarla en contratos.">
              <Field label="Nombre" required>
                <Input
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  maxLength={255}
                  required
                  autoFocus
                  placeholder="Ej. Estándar 22 días"
                />
              </Field>
            </SectionCard>

            <SectionCard title={SECTION_TITLES[1].label} hint="Cuándo empieza y cuánto dura el ciclo de devengo.">
              <Grid2>
                <Field label="El ciclo comienza en">
                  <Select
                    value={form.cycle_start_month}
                    onChange={(e) => set({ cycle_start_month: Number(e.target.value) })}
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Duración del ciclo (meses)">
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={form.cycle_duration_months}
                    onChange={(e) => set({ cycle_duration_months: Number(e.target.value) })}
                  />
                </Field>
              </Grid2>
            </SectionCard>

            <SectionCard title={SECTION_TITLES[2].label} hint="Cómo se mide y se acumula el saldo.">
              <Grid2>
                <Field label="El contador se establece en">
                  <Select
                    value={form.accrual_type}
                    onChange={(e) => set({ accrual_type: e.target.value })}
                  >
                    {ACCRUAL_UNITS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="¿Cómo mostrar?">
                  <Select
                    value={form.display_unit}
                    onChange={(e) => set({ display_unit: e.target.value })}
                  >
                    {DISPLAY_UNITS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Días base del contador" required>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.allowance_per_year}
                    onChange={(e) => set({ allowance_per_year: e.target.value })}
                    disabled={form.unlimited}
                  />
                </Field>
                <Field label="Cuenta como">
                  <Select
                    value={form.day_count_basis}
                    onChange={(e) => set({ day_count_basis: e.target.value })}
                  >
                    {DAY_COUNT_BASES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
              </Grid2>

              <CheckRow
                checked={form.unlimited}
                onChange={(v) => set({ unlimited: v })}
                label="Días ilimitados"
                hint="Si se activa, ignora el número de días base."
              />
              <CheckRow
                checked={form.holiday_inside_absence_counts}
                onChange={(v) => set({ holiday_inside_absence_counts: v })}
                label="Si un festivo cae en la ausencia, cuenta como día consumido"
                hint="Si está apagado, el festivo no se descuenta del saldo."
              />
              <CheckRow
                checked={form.prorate_first_year}
                onChange={(v) => set({ prorate_first_year: v })}
                label="Prorratear el primer ciclo"
                hint="Para empleados que entran a mitad de ciclo se calcula la parte proporcional."
              />
              <CheckRow
                checked={form.allow_usage_in_next_cycle}
                onChange={(v) => set({ allow_usage_in_next_cycle: v })}
                label="Pueden usarse en el siguiente ciclo"
              />
              <CheckRow
                checked={form.allow_negative_balance}
                onChange={(v) => set({ allow_negative_balance: v })}
                label="Permitir saldo negativo"
                hint="El empleado puede solicitar más días de los disponibles."
              />

              <Grid2>
                <Field label="Cómo se generan los días">
                  <Select
                    value={form.accrual_mode}
                    onChange={(e) => set({ accrual_mode: e.target.value })}
                  >
                    {ACCRUAL_MODES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Máximo del contador" help="Vacío = sin límite.">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.max_balance ?? ""}
                    onChange={(e) =>
                      set({ max_balance: e.target.value === "" ? null : e.target.value })
                    }
                    placeholder="Ilimitado"
                  />
                </Field>
              </Grid2>
            </SectionCard>

            <SectionCard title={SECTION_TITLES[3].label} hint="Permite arrastrar saldo no usado al siguiente ciclo.">
              <Grid2>
                <Field label="Días que se acumulan">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.carryover_amount}
                    onChange={(e) => set({ carryover_amount: e.target.value })}
                    disabled={form.carryover_unlimited}
                  />
                </Field>
                <Field
                  label="¿Cuándo vencen?"
                  help="Meses tras el final del ciclo previo."
                >
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={form.carryover_expiration_months ?? ""}
                    onChange={(e) =>
                      set({
                        carryover_expiration_months:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    disabled={form.carryover_unlimited}
                    placeholder="Sin vencimiento"
                  />
                </Field>
              </Grid2>
              <CheckRow
                checked={form.carryover_unlimited}
                onChange={(v) => set({ carryover_unlimited: v })}
                label="Acumulación ilimitada"
              />
            </SectionCard>

            <SectionCard title={SECTION_TITLES[4].label} hint="Días mínimos de aviso para solicitar la ausencia.">
              <Grid2>
                <Field label="Antelación mínima (días)">
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={form.min_notice_days}
                    onChange={(e) => set({ min_notice_days: e.target.value })}
                  />
                </Field>
                <div />
              </Grid2>
            </SectionCard>

            <SectionCard
              title={SECTION_TITLES[5].label}
              hint="Días extra otorgados según los años en la empresa."
              action={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon="Plus"
                  onClick={addTenurePeriod}
                >
                  Añadir tramo
                </Button>
              }
            >
              {(form.tenure_periods || []).length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "4px 0" }}>
                  Sin tramos definidos. Pulsa "Añadir tramo" para sumar días según antigüedad.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {form.tenure_periods.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 140px 36px",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <Field label={i === 0 ? "Tras N años" : null}>
                        <Input
                          type="number"
                          min={1}
                          max={60}
                          value={p.years_of_service ?? ""}
                          onChange={(e) =>
                            updateTenurePeriod(i, { years_of_service: e.target.value })
                          }
                        />
                      </Field>
                      <Field label={i === 0 ? "Días extra" : null}>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={p.extra_amount ?? ""}
                          onChange={(e) =>
                            updateTenurePeriod(i, { extra_amount: e.target.value })
                          }
                        />
                      </Field>
                      <Field label={i === 0 ? "Unidad" : null}>
                        <Select
                          value={p.unit || "days"}
                          onChange={(e) => updateTenurePeriod(i, { unit: e.target.value })}
                        >
                          {ACCRUAL_UNITS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Select>
                      </Field>
                      <div style={{ alignSelf: i === 0 ? "end" : "center" }}>
                        <IconBtn icon="Trash2" onClick={() => removeTenurePeriod(i)} title="Quitar" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear política"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

function SectionCard({ title, hint, action, children }) {
  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
            {title}
          </div>
          {hint && (
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
              {hint}
            </div>
          )}
        </div>
        {action}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </Card>
  );
}

function Grid2({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {children}
    </div>
  );
}

function CheckRow({ checked, onChange, label, hint }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        cursor: "pointer",
        padding: "6px 0",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, width: 16, height: 16, accentColor: "var(--kr-action)" }}
      />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
          {label}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            {hint}
          </div>
        )}
      </div>
    </label>
  );
}

export default VacationPolicyEditorModal;
